import { AutocompleteSelect, Banner, BannerVariant, Form, FormSection, Modal, ModalSizes, RadioButton, Text, Textarea, TextInput } from "@centricsoftware/design-system";
import { CANCEL_RES, SAVE_RES } from "csi/components/constants";
import { RuleStoreContext } from "csi/middleware/attributeRule/RuleStore";
import CustomViewFilterPane from "csi/view/customview/CustomViewFilterPane";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import DataStoreDialogStore from "./DataStoreDialogStore";

interface DataStoreDialogProps {
    /** determines if dialog is open */
    open?: boolean; 

    /** store to handle operations done in the dialog */
    store: DataStoreDialogStore;
}

const DataStoreForm: FunctionComponent<DataStoreDialogProps> = observer((props) => {
	const {
		description,
		isGlobal,
		limitPathErrorRes,
		limitPaths,
		name,
		nameErrorRes,
		onChangeName,
		onChangeDescription,
		onChangeLimitPath,
		onChangePathHints,
		onChangeScope,
		pathHints,
		pathHintsOptions,
		ruleStore,
		scope,
		scopeOptions,
		switchBackToGlobal,
		targetClass,
		validationErrorRes,
	} = props.store;

	return (
		<Form isFullWidth>
			{validationErrorRes && (
				<Banner
					bodyRes={validationErrorRes}
					variant={BannerVariant.Error}
				/>
			)}
			<TextInput
				labelRes={{ res: "TargetClass" }}
				readOnly
				value={targetClass}
			/>
			<RadioButton
				labelRes={{ res: "DataStoreScope" }}
				onChange={onChangeScope}
				options={scopeOptions}
				value={scope}
			/>
			<TextInput
				error={!!nameErrorRes}
				errorMessageRes={nameErrorRes}
				labelRes={{ res: "Name" }}
				onBlurChange={onChangeName}
				required
				value={name}
			/>
			<Textarea
				labelRes={{ res: "DataStoreDefinition.Attr.Description" }}
				onBlurChange={onChangeDescription}
				value={description}
			/>
			{(!isGlobal || switchBackToGlobal) && (
				<Textarea
					error={!!limitPathErrorRes}
					errorMessageRes={limitPathErrorRes}
					helperTextRes={{ res: "DataStoreOnePerLine" }}
					labelRes={{ res: "DataStoreDefinition.Attr.LimitPaths" }}
					onBlurChange={onChangeLimitPath}
					value={limitPaths}
				/>
			)}
			<AutocompleteSelect
				dropdownButtonAriaLabelRes={{ res: "DataStoreDefinition.Attr.PathHints" }}
				labelRes={{ res: "DataStoreDefinition.Attr.PathHints" }}
				onChange={onChangePathHints}
				options={pathHintsOptions}
				value={pathHints}
			/>
			{ruleStore && (
				<RuleStoreContext.Provider value={ruleStore}>
					<FormSection titleRes={{ res: "DataStoreQueryFilters" }} />
					<CustomViewFilterPane />
				</RuleStoreContext.Provider>
			)}
		</Form>
	);
});

const DataStoreSwitchBackToGlobal: FunctionComponent<DataStoreDialogProps> = observer((props) => {
	const {
		switchBackToGlobal,
		onCancelSwitchBackToGlobal,
		onContinueSwitchBackToGlobal,
	} = props.store;

	return (
		<Modal
			actionButtonProps={{
				childrenRes: { res: "BUTTON_CONT" },
				onClick: onContinueSwitchBackToGlobal,
			}}
			cancelButtonProps={{
				childrenRes: { res: "BUTTON_C" },
				onClick: onCancelSwitchBackToGlobal,
			}}
			icon="warning"
			onClose={onCancelSwitchBackToGlobal}
			open={switchBackToGlobal}
			showActionButton
			showCancelButton
			showFooter
			titleRes={{ res: "DataStoreSwitchBackToGlobalModal" }}
		>
			<Text
				childrenRes={{ res: "DataStoreSwitchBackToGlobalModalContent" }}
			/>
		</Modal>
	);
});

const DataStoreSaveAsGlobal: FunctionComponent<DataStoreDialogProps> = observer((props) => {
	const {
		saveAsGlobal,
		onCancelSaveAsGlobal,
		onContinueSaveAsGlobal,
	} = props.store;

	return (
		<Modal
			actionButtonProps={{
				childrenRes: { res: "BUTTON_CONT" },
				onClick: onContinueSaveAsGlobal,
			}}
			cancelButtonProps={{
				childrenRes: { res: "BUTTON_C" },
				onClick: onCancelSaveAsGlobal,
			}}
			icon="warning"
			onClose={onCancelSaveAsGlobal}
			open={saveAsGlobal}
			showActionButton
			showCancelButton
			showFooter
			titleRes={{ res: "DataStoreSaveAsGlobalModal" }}
		>
			<Text
				childrenRes={{ res: "DataStoreSaveAsGlobalModalContent" }}
			/>
		</Modal>
	);
});

const DataStoreDialog: FunctionComponent<DataStoreDialogProps> = observer((props) => {
	const { open, store } = props;
	const { onCancel, onSave } = store;

	return (
		<Modal
			actionButtonProps={{
				childrenRes: SAVE_RES,
				onClick: onSave,
			}}
			cancelButtonProps={{
				childrenRes: CANCEL_RES,
				onClick: onCancel,
			}}
			dataCsiAutomation="DataStoreDialog"
			onClose={onCancel}
			open={open}
			showActionButton
			showCancelButton
			showFooter
			size={ModalSizes.ExtraLarge}
			titleRes={{ res: "AggregateStoreGroupNewDataStore" }}
		>
			<>
				<DataStoreForm store={store} />
				<DataStoreSaveAsGlobal store={store} />
				<DataStoreSwitchBackToGlobal store={store} />
			</>
		</Modal>
	);
});

export default DataStoreDialog;